import { Component, inject } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FlagUnpaidLicensePayload } from '../../../model/user.model';
import { MdcDialogComponent } from '../../../components/mdc/mdc-dialog/mdc-dialog.component';
import moment, { Moment } from 'moment';
import { InfosGuidanceComponent } from '../../../components/infos-guidance/infos-guidance.component';
import { MatInput } from '@angular/material/input';
import { DateInputHelper } from '../../../helpers/date-input.helper';
import { MatDatepickerModule } from '@angular/material/datepicker';

@Component({
  selector: 'flag-unpaid-license',
  imports: [
    MatFormFieldModule,
    ReactiveFormsModule,
    MatDialogModule,
    MdcDialogComponent,
    InfosGuidanceComponent,
    MatFormFieldModule,
    MatInput,
    MatDatepickerModule
  ],
  template: `
    <mdc-dialog
      dialogTitle="Flag Unpaid License"
      [closeOnSave]="false"
      [disableSave]="form.invalid"
      (save)="dialogRef.close(flagData)">
      <ng-template #contentTemplate>
        <section class="flex flex-col items-center gap-y-30 -mb-10 overflow-hidden">
          <infos-guidance [guidanceText]="guidance" />

          <div class="grid grid-cols-3 gap-20" [formGroup]="form">
            <mat-form-field>
              <mat-label>Invoice Number</mat-label>
              <input formControlName="invoiceNumber" matInput required />
              @if (invoiceNumber.invalid) {
                <mat-error> Required </mat-error>
              }
            </mat-form-field>

            <mat-form-field>
              <mat-label>Due Date</mat-label>
              <input
                matInput
                formControlName="dueDate"
                (keypress)="dateInputHelper.restrictDateInput($event)"
                [max]="today"
                [matDatepicker]="dueDatePicker" />
              <mat-datepicker-toggle [for]="dueDatePicker" matSuffix />
              <mat-datepicker #dueDatePicker />
              <mat-hint>DD/MM/YYYY</mat-hint>
              @let dueErrorMessage =
                dueDate.hasError('matDatepickerParse')
                  ? 'Invalid date format'
                  : dueDate.hasError('matDatepickerMax')
                    ? 'Date must be before today'
                    : dueDate.hasError('required')
                      ? 'Required'
                      : '';
              <mat-error>{{ dueErrorMessage }}</mat-error>
            </mat-form-field>

            <mat-form-field>
              <mat-label>Suspension Date</mat-label>
              <input
                matInput
                formControlName="suspensionDate"
                (keypress)="dateInputHelper.restrictDateInput($event)"
                [min]="today"
                [matDatepicker]="suspensionDatePicker" />
              <mat-datepicker-toggle [for]="suspensionDatePicker" matSuffix />
              <mat-datepicker #suspensionDatePicker />
              <mat-hint>DD/MM/YYYY</mat-hint>
              @let suspensionErrorMessage =
                suspensionDate.hasError('matDatepickerParse')
                  ? 'Invalid date format'
                  : suspensionDate.hasError('matDatepickerMin')
                    ? 'Date must be after today'
                    : suspensionDate.hasError('required')
                      ? 'Required'
                      : '';
              <mat-error>{{ suspensionErrorMessage }}</mat-error>
            </mat-form-field>
          </div>
        </section>
      </ng-template>
    </mdc-dialog>
  `
})
export class FlagUnpaidLicenseComponent {
  dialogRef = inject(MatDialogRef<FlagUnpaidLicenseComponent>);
  #fb = inject(FormBuilder);
  #data = inject(MAT_DIALOG_DATA);
  dateInputHelper = inject(DateInputHelper);
  today: Moment = moment();
  form = this.#fb.group({
    invoiceNumber: ['', Validators.required],
    dueDate: [null, Validators.required],
    suspensionDate: [null, Validators.required]
  });

  guidance = `Let ${this.#data.companyName} know their license fee is overdue.`;

  get flagData(): FlagUnpaidLicensePayload {
    const { invoiceNumber, dueDate, suspensionDate } = this.form.getRawValue();
    return {
      invoiceNumber,
      dueDate: dueDate ? (dueDate as Moment).format('DD/MM/YYYY') : '',
      suspensionDate: suspensionDate ? (suspensionDate as Moment).format('DD/MM/YYYY') : ''
    };
  }

  get invoiceNumber(): FormControl<string> {
    return this.form.controls.invoiceNumber as FormControl<string>;
  }
  get dueDate(): FormControl<string> {
    return this.form.controls.dueDate as FormControl<string>;
  }
  get suspensionDate(): FormControl<string> {
    return this.form.controls.suspensionDate as FormControl<string>;
  }
}
